// Angular import
import { Component, OnInit, OnDestroy, NgZone, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { get, post1 } from 'src/utils/api';
import { APP_API_URL, APP_HD_URL1 } from 'src/utils/urls';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DataSharingService } from 'src/utils/broadcast-service';
import { SharedService } from 'src/app/activity.service';
import { Subscription } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ThemeService } from 'src/utils/theme.service';
import { IncomingReferralService } from 'src/app/demo/incomingReferral/incoming-referral.service';
import { ExceptionQueueService } from 'src/app/demo/exceptionQueue/exception-queue.service';
import { PacketBucketService } from 'src/app/demo/packetBuckets/packet-bucket.service';
import { ALL_REFERRALS_BUCKET, DELETED_BUCKET, PACKET_BUCKET_ITEMS } from 'src/app/demo/packetBuckets/packet-bucket.models';
import { SignalRService } from 'src/app/services/signalr.service';
import { FilterService } from 'src/app/services/filter.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-nav-right',
  templateUrl: './nav-right.component.html',
  styleUrls: ['./nav-right.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NavRightComponent implements OnInit, OnDestroy {
  hasFilter = false;
  pillText: string | null = null;
  private filterSub: Subscription | null = null;
  private pillSub: Subscription | null = null;

  constructor(
    private router: Router,
    private message: NzMessageService,
    private dataSharingService: DataSharingService,
    private sharedService: SharedService,
    private ngZone: NgZone,
    private modal: NzModalService,
    private themeService: ThemeService,
    private referralSvc: IncomingReferralService,
    private excSvc: ExceptionQueueService,
    private packetSvc: PacketBucketService,
    private signalR: SignalRService,
    private filterSvc: FilterService,
    private authService: AuthService
  ) {}

  // ── Notification state ──────────────────────────────────────────────────
  notifOpen = false;
  // The panel's referral rows are the Packet Buckets menu (same items as the sidebar), each opening
  // Incoming Referrals scoped to that bucket — minus System Error, Unmapped and Deleted, which stay
  // sidebar-only. All referrals still counts the first two, same as the sidebar's All referrals.
  readonly packetItems = PACKET_BUCKET_ITEMS.filter((p) => !['System Error', 'Unmapped', DELETED_BUCKET].includes(p.bucket));
  readonly allReferralsBucket = ALL_REFERRALS_BUCKET;
  packetCounts: Record<string, number> = {};
  // Only feeds the bell badge now (on-hold referrals + exceptions) — no panel row shows it.
  referralSummary = { totalReferrals: 0, pending: 0, inProgress: 0, completed: 0, highPriority: 0, missingDocs: 0 };
  excSummary = {
    total: 0,
    legalGuardian: 0,
    authorization: 0,
    facilityChange: 0,
    missingDocs: 0,
    other: 0,
    nonReferrals: 0,
    duplicate: 0,
    nonReferral: 0
  };

  get notifBadge(): number {
    return (this.referralSummary.pending || 0) + (this.excSummary.total || 0);
  }

  private signalRSub1: Subscription | null = null;
  private signalRSub2: Subscription | null = null;

  // Both SignalR pushes and the notification-panel toggle call loadCounts(), so two can overlap
  // and land out of order, leaving the badge on the older counts. One controller covers all the
  // summary calls, since they are awaited and applied together.
  private countsAbort: AbortController | null = null;

  username = '';
  email = '';
  userInitials = '';
  roleDisplay = '';
  tenantName = '';
  clientsList: any[] = [];
  selectedClient: string = '';
  isCPVisible = false;
  isTFAVisible = false;
  showTFA = false;
  isDark = false;
  showManagement = false;
  mfaEnabled = localStorage.getItem('regTFA') ? localStorage.getItem('regTFA') === 'yes' : false;
  subscription: Subscription;
  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (this.notifOpen && !(e.target as HTMLElement).closest('.notif-wrap')) {
      this.notifOpen = false;
    }
  }

  toggleNotif(e: MouseEvent) {
    e.stopPropagation();
    this.notifOpen = !this.notifOpen;
    if (this.notifOpen) this.loadCounts();
  }

  async loadCounts() {
    this.countsAbort?.abort();
    const controller = new AbortController();
    this.countsAbort = controller;

    try {
      const [rRes, eRes, packetCounts]: any[] = await Promise.all([
        this.referralSvc.getSummary(undefined, controller.signal),
        this.excSvc.getSummary(controller.signal),
        this.packetSvc.getBucketCounts(controller.signal)
      ]);
      if (controller.signal.aborted) return;
      if (rRes?.data?.data) this.referralSummary = rRes.data.data;
      if (eRes?.data?.data) this.excSummary = eRes.data.data;
      this.packetCounts = packetCounts;
    } catch {
      /* silent */
    }
  }

  ngOnInit() {
    this.tenantName = localStorage.getItem('tenantName') || '';
    this.isDark = this.themeService.isDark;
    if (localStorage.getItem('userInfo')) {
      let userData = JSON.parse(localStorage.getItem('userInfo'));
      this.username = userData?.FirstName + ' ' + userData?.LastName;
      this.email = userData?.EmailId;
      this.selectedClient = userData?.ClientId;
      this.userInitials = ((userData?.FirstName?.charAt(0) || '') + (userData?.LastName?.charAt(0) || '')).toUpperCase();

      this.showTFA = localStorage.getItem('regTFA') ? localStorage.getItem('regTFA') === 'yes' : false;
    }

    const roles = this.authService.getRoles();
    this.roleDisplay = roles.length > 0 ? roles.join(', ') : 'User';
    this.showManagement = this.authService.hasRole(['SystemAdmin', 'Administrator']);

    // if (localStorage.getItem('role') === 'Super Admin') {
    // this.getClientsList(false);
    // }

    // Track whether the current page has registered filters
    this.filterSub = this.filterSvc.template$.subscribe((t) => (this.hasFilter = t !== null));

    // Track the current page's date-range pill (e.g. document dashboard)
    this.pillSub = this.filterSvc.pill$.subscribe((text) => (this.pillText = text));

    // Wire SignalR for live notification counts
    this.loadCounts();
    this.signalRSub1 = this.signalR.referralQueueChanged$.subscribe(() => this.loadCounts());
    this.signalRSub2 = this.signalR.exceptionSummaryChanged$.subscribe(() => this.loadCounts());

    this.subscription = this.dataSharingService.regTFAService$.subscribe((data) => {
      if (data === 'yes') {
        this.mfaEnabled = true;
      }
    });

    this.subscription = this.dataSharingService.updateClients$.subscribe((data) => {
      if (data) {
        this.getClientsList(true);
      }
    });
  }

  openFilter(): void {
    this.filterSvc.open();
  }

  ngOnDestroy() {
    this.filterSub?.unsubscribe();
    this.pillSub?.unsubscribe();
    this.signalRSub1?.unsubscribe();
    this.signalRSub2?.unsubscribe();
    this.countsAbort?.abort();
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.dataSharingService.clearRegTFA();
      this.dataSharingService.clearClientService();
    }
  }

  onSelectedClient = (e) => {
    this.selectedClient = e;
    const foundItem = this.clientsList.find((item) => item.id === parseInt(e));
    if (foundItem?.abbreviation) {
      localStorage.setItem('abbr', foundItem?.abbreviation);
      localStorage.setItem('clientId', e);
    }

    this.dataSharingService.updateData({ id: e, refresh: false });
  };

  getClientsList = async (update) => {
    let res: any = await get(APP_API_URL + 'Clients/GetClientsDropdown');
    if (res?.status === 200) {
      this.clientsList = res.data;
      if (this.clientsList.length > 0 && !update) {
        let urlAbbrId = null;
        let urlAbbr = null;
        if (!update) {
          urlAbbrId =
            localStorage.getItem('clientId') && localStorage.getItem('clientId') !== '0' ? localStorage.getItem('clientId') : null;
          urlAbbr = localStorage.getItem('abbr') ? localStorage.getItem('abbr') : null;
        }
        if (localStorage.getItem('role') === 'Super Admin') {
          localStorage.setItem('abbr', urlAbbr ? urlAbbr : this.clientsList[0].abbreviation);
          localStorage.setItem('clientId', urlAbbrId ? urlAbbrId : this.clientsList[0].id);

          this.dataSharingService.updateData({
            id: urlAbbrId ? urlAbbrId.toString() : this.clientsList[0].id.toString(),
            refresh: false,
            init: update
          });
          this.selectedClient = urlAbbrId ? urlAbbrId.toString() : this.clientsList[0].id.toString();
        } else {
          let ind = this.clientsList.findIndex((d) => d.isDefault);
          if (ind !== -1) {
            localStorage.setItem('abbr', urlAbbr ? urlAbbr : this.clientsList[ind].abbreviation);
            localStorage.setItem('clientId', urlAbbrId ? urlAbbrId : this.clientsList[ind].id);

            this.dataSharingService.updateData({
              id: urlAbbrId ? urlAbbrId.toString() : this.clientsList[ind].id.toString(),
              refresh: false,
              init: update
            });
            this.selectedClient = urlAbbrId ? urlAbbrId.toString() : this.clientsList[ind].id.toString();
          }
        }
      }
    }
  };

  onLogoutClick = async () => {
    // The client-side session ends regardless of whether the server-side revoke
    // call succeeds, so the user always sees a clean "signed out" confirmation
    // rather than a raw error about a backend call they didn't initiate.
    await post1(APP_HD_URL1 + 'api/Authentication/logout', {});

    this.message.create('success', 'You have been signed out successfully.');
    localStorage.clear();
    sessionStorage.clear();
    this.themeService.reset();
    // Full reload (not router.navigate) so the SPA's in-memory JS state — the axios
    // instance, its interceptors, module-level vars in api.ts — is torn down and
    // rebuilt clean, same as the forced session-end redirect in api.ts does.
    window.location.href = '/guest/login';
  };

  onChangePwd = () => {
    this.sharedService.auditLog('change password');
    this.isCPVisible = true;
  };

  onCPCloseModalEvent = (e) => {
    this.isCPVisible = false;
  };

  onTFASettings = () => {
    // this.sharedService.auditLog('TFA settings');
    // this.isTFAVisible = true;
    this.modal.confirm({
      nzTitle: `<h3>${this.showTFA ? 'Disable MFA' : 'Enable MFA'} </h3>`,
      nzContent: `Are you sure want to ${this.showTFA ? 'disable' : 'enable'} MFA?`,
      nzOnOk: () => this.enableDisableMFA()
    });
  };

  onTFACloseModalEvent = (e) => {
    this.isTFAVisible = false;
  };

  toggleDark = () => {
    this.themeService.toggleDark();
    this.isDark = this.themeService.isDark;
  };

  enableDisableMFA = async () => {
    let userInfo = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')) : '';
    let data = { userName: userInfo.email, useMfa: !this.showTFA };
    let res: any = await post1(APP_HD_URL1 + 'api/Authentication/mfa', data);
    if (res.status === 200) {
      this.message.create('success', res?.data?.message ? res?.data?.message : 'MFA status updated successfully');
      this.showTFA = !this.showTFA;
      localStorage.setItem('regTFA', this.showTFA ? 'yes' : '');
    }
  };
}
